import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isTestEnv as IS_TEST_ENV } from "@/lib/env";

interface ResolveTerminalPollingIntervalInput {
  intervalMs: number;
  /** The resource has reached a state it will not leave. */
  isTerminal: boolean;
  /** The cap ran out before the resource reached a terminal state. */
  hasGivenUp: boolean;
  isTestEnv: boolean;
  /** Poll even in the E2E/Storybook build. Off by default. */
  enabledInTestEnv: boolean;
}

/**
 * The interval a poll waiting for a terminal state should run at, or
 * `undefined` to not poll. RTK Query's `pollingInterval` and a hand-rolled
 * `setInterval` both read "no interval" as "don't poll".
 */
export function resolveTerminalPollingInterval({
  intervalMs,
  isTerminal,
  hasGivenUp,
  isTestEnv,
  enabledInTestEnv,
}: ResolveTerminalPollingIntervalInput): number | undefined {
  if (isTerminal) return undefined;
  if (hasGivenUp) return undefined;
  // The test build keeps the network quiet so `networkidle` waits settle.
  if (isTestEnv && !enabledInTestEnv) return undefined;
  return intervalMs;
}

interface UseTerminalPollingGateOptions {
  intervalMs: number;
  /**
   * Stop polling once this long has passed since polling started for the
   * current resource. Opt in only where the page can say it stopped checking
   * and offer a way to check again (`restart`).
   */
  capMs?: number;
  enabledInTestEnv?: boolean;
}

export interface TerminalPollingGate {
  /** Pass straight to RTK Query's `pollingInterval`, or a `setInterval`. */
  pollingInterval: number | undefined;
  /**
   * Report whether the resource is terminal. Call it whenever the reading may
   * have changed; a call that repeats the last reading does nothing.
   */
  update: (isTerminal: boolean) => void;
  /**
   * The cap ran out while the resource was still moving. Information for the
   * page to show, not an error: the resource itself is unaffected.
   */
  hasGivenUp: boolean;
  /** Clear a give-up and start a new cap window. */
  restart: () => void;
}

interface CapWindow {
  hasGivenUp: boolean;
  /** Bumped by `restart` so the cap timer starts over. */
  generation: number;
}

const FIRST_CAP_WINDOW: CapWindow = { hasGivenUp: false, generation: 0 };

/**
 * Decides when a poll that waits for a terminal state stops: when the call site
 * reports the resource terminal, when the optional cap runs out, or always in
 * the test build unless the call site opts in.
 *
 * Starts terminal, so nothing polls until the call site first reports a
 * resource that is still moving. The cap is measured from that moment (and
 * from each `restart`), not from mount: the timer starts when the resolved
 * interval goes from `undefined` to a number and is cleared whenever it goes
 * back. A give-up lasts until `restart` or the next flip of the terminal
 * reading, so a resource that turns terminal and later moves again polls under
 * a fresh cap. React state changes only when the terminal reading flips, on
 * give-up and on restart; a poll tick that repeats the last reading sets
 * nothing.
 */
export function useTerminalPollingGate({
  intervalMs,
  capMs,
  enabledInTestEnv = false,
}: UseTerminalPollingGateOptions): TerminalPollingGate {
  const [isTerminal, setIsTerminal] = useState(true);
  const [capWindow, setCapWindow] = useState<CapWindow>(FIRST_CAP_WINDOW);

  // The last reading `update` saw, so a repeat returns before touching state.
  // Read and written only inside the callback, never during render.
  const lastReadingRef = useRef(true);
  const update = useCallback((nextIsTerminal: boolean) => {
    if (lastReadingRef.current === nextIsTerminal) return;
    lastReadingRef.current = nextIsTerminal;
    setIsTerminal(nextIsTerminal);
    // A flipped reading ends the run the give-up belonged to, so the next run
    // starts with a fresh cap window. Returning `prev` keeps a run that never
    // gave up from setting state here.
    setCapWindow((prev) =>
      prev.hasGivenUp
        ? { hasGivenUp: false, generation: prev.generation + 1 }
        : prev
    );
  }, []);

  const restart = useCallback(() => {
    setCapWindow((prev) => ({
      hasGivenUp: false,
      generation: prev.generation + 1,
    }));
  }, []);

  const pollingInterval = resolveTerminalPollingInterval({
    intervalMs,
    isTerminal,
    hasGivenUp: capWindow.hasGivenUp,
    isTestEnv: IS_TEST_ENV,
    enabledInTestEnv,
  });
  const isPolling = pollingInterval !== undefined;
  const { generation } = capWindow;

  useEffect(() => {
    if (!isPolling || capMs === undefined) return;
    const timer = setTimeout(() => {
      setCapWindow((prev) =>
        prev.hasGivenUp ? prev : { ...prev, hasGivenUp: true }
      );
    }, capMs);
    return () => clearTimeout(timer);
  }, [isPolling, capMs, generation]);

  // A give-up that a later read found terminal has nothing left to say.
  const hasGivenUp = capWindow.hasGivenUp && !isTerminal;

  // One object per distinct reading, so a consumer can hold the whole gate in
  // a dep array or a context value without churning every render.
  return useMemo(
    () => ({ pollingInterval, update, hasGivenUp, restart }),
    [pollingInterval, update, hasGivenUp, restart]
  );
}
